import * as E from 'fp-ts/Either';
import { stringify } from 'qs';

export type AllowNullKeys<T> = {
  [K in keyof T]: T[K] | null;
};

// We don't allow $regex over the API as it can blow things up in mongodb
export type CustomFilter = string | string[] | { $partial: string };

export type ErrorResponse = { message?: string; helpMessage?: string };

export type PagedResponse<T> = {
  prev: null | string;
  next: null | string;
  data: T[];
};

export type RecordPager<T> = (
  token: string,
  from: null | string,
  filter?: Record<string, CustomFilter>
) => Promise<E.Either<ErrorResponse, PagedResponse<T>>>;

export const page =
  <T>(baseUrl: string, format?: (item: unknown) => T): RecordPager<T> =>
  async (token, from, filter) => {
    const query = filter ? stringify({ filter }) : '';

    const hostUrl = from ? `${baseUrl}?from=${from}` : baseUrl;

    const url = !query
      ? hostUrl
      : hostUrl.includes('?')
      ? `${hostUrl}&${query}`
      : `${hostUrl}?${query}`;

    const result = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const body = await result.json();

    if (result.status !== 200) {
      return E.left(body);
    }

    return format
      ? E.right({ ...body, data: body.data.map(format) })
      : E.right(body);
  };

export const removeItem =
  (baseUrl: string) =>
  async (token: string, id: string): Promise<E.Either<Error, void>> => {
    const response = await fetch(`${baseUrl}/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });

    if (response.ok) {
      return E.right(undefined);
    }

    return E.left(new Error('Failed to remove item'));
  };

export const singleItem =
  <T>(baseUrl: string) =>
  async (token: string, id: string): Promise<E.Either<Error, T>> => {
    try {
      const response = await fetch(`${baseUrl}/${encodeURIComponent(id)}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });

      const body = await response.json();

      if (response.status === 200) {
        return E.right(body);
      }

      return E.left(new Error(body.helpMessage ?? 'Failed to fetch item'));
    } catch (e) {
      console.log(e);
      return E.left(new Error('Failed to fetch item'));
    }
  };

export const insertItem =
  <T>(url: string) =>
  async (token: string, record: Partial<T>): Promise<E.Either<Error, T>> => {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(record),
    });

    const body = await response.json();

    if (response.status === 200) {
      return E.right(body as T);
    }

    return E.left(new Error(body.helpMessage ?? 'Failed to insert item'));
  };

export const updateItem =
  <T>(baseUrl: string) =>
  async (
    token: string,
    id: string,
    record: Partial<AllowNullKeys<T>> // we allow nulls here so that we can unset fields
  ): Promise<E.Either<Error, T>> => {
    try {
      const response = await fetch(`${baseUrl}/${encodeURIComponent(id)}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(record),
      });

      const body = await response.json();

      if (response.status === 200) {
        return E.right(body as T);
      }

      return E.left(new Error(body.helpMessage ?? 'Failed to update item'));
    } catch (e) {
      console.log(e);
      return E.left(new Error('Failed to update item'));
    }
  };
